/**
 * Phase S1 — Dashboard preferences modal.
 *
 * Mounted from the user-menu (super_admin + admin). Lets the operator
 * show/hide cards on the admin dashboard without leaving their current
 * page. Backed by the same /admin/dashboard-preferences endpoint as the
 * inline Customize mode on the dashboard itself.
 *
 * The user-menu dispatches a window event ("open-dashboard-prefs") to
 * unmount/mount this dialog so a single instance lives in <App> rather
 * than re-instantiating per dropdown item — keeps fetch state warm.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    DASHBOARD_CARDS,
    DASHBOARD_TABS,
    DEFAULT_HIDDEN_CARD_KEYS,
} from "@/lib/dashboardCards";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const OPEN_DASHBOARD_PREFS_EVENT = "open-dashboard-prefs";

export function DashboardPreferencesDialog() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hidden, setHidden] = useState(() => new Set(DEFAULT_HIDDEN_CARD_KEYS));

    // Listen for the user-menu open trigger.
    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener(OPEN_DASHBOARD_PREFS_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_DASHBOARD_PREFS_EVENT, onOpen);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/dashboard-preferences");
            setHidden(new Set(data?.hidden_cards || []));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open) load(); }, [open, load]);

    const isSuper = user?.role === "super_admin";

    const visibleCards = useMemo(
        () => DASHBOARD_CARDS.filter((c) => !c.requiresSuperAdmin || isSuper),
        [isSuper],
    );

    const persistHidden = useCallback(async (next) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/dashboard-preferences", {
                hidden_cards: Array.from(next),
            });
            setHidden(new Set(data?.hidden_cards || []));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }, []);

    const toggle = (cardKey, shouldHide) => {
        const next = new Set(hidden);
        if (shouldHide) next.add(cardKey);
        else next.delete(cardKey);
        setHidden(next);
        persistHidden(next);
    };

    const resetAll = async () => {
        const next = new Set(DEFAULT_HIDDEN_CARD_KEYS);
        setHidden(next);
        await persistHidden(next);
        toast.success("Dashboard preferences reset to defaults.");
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                className="max-w-2xl"
                data-testid="dashboard-prefs-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display">Dashboard preferences</DialogTitle>
                    <DialogDescription>
                        Toggle which cards appear on your admin dashboard. Changes save
                        automatically and only affect your own view.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 text-center text-sm text-slate-500" data-testid="dashboard-prefs-loading">
                        Loading…
                    </div>
                ) : (
                    <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-5">
                        {DASHBOARD_TABS.map((tab) => {
                            const cards = visibleCards.filter((c) => c.tab === tab.id);
                            if (cards.length === 0) return null;
                            return (
                                <div key={tab.id} data-testid={`dashboard-prefs-tab-${tab.id}`}>
                                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                        {tab.label}
                                    </h3>
                                    <ul className="rounded-md border border-slate-200 divide-y divide-slate-100">
                                        {cards.map((card) => {
                                            const isHidden = hidden.has(card.key);
                                            return (
                                                <li
                                                    key={card.key}
                                                    className="flex items-center justify-between gap-3 px-3 py-2.5"
                                                    data-testid={`dashboard-prefs-row-${card.key}`}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="text-sm text-slate-900 truncate">
                                                            {card.label}
                                                        </div>
                                                        {card.requiresSuperAdmin && (
                                                            <div className="text-[10px] uppercase tracking-wider text-blue-900">
                                                                Super admin
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Switch
                                                        checked={!isHidden}
                                                        onCheckedChange={(checked) => toggle(card.key, !checked)}
                                                        disabled={saving}
                                                        data-testid={`dashboard-prefs-toggle-${card.key}`}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                )}

                <DialogFooter className="flex justify-between gap-2 mt-4">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={resetAll}
                        disabled={loading || saving}
                        data-testid="dashboard-prefs-reset"
                    >
                        Reset to defaults
                    </Button>
                    <Button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="dashboard-prefs-done"
                    >
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DashboardPreferencesDialog;
